import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  CircularProgress,
  OutlinedInput,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { FormikProps, useFormik } from "formik";
import { useEffect, useState } from "react";
import { sxFormError } from "../../../../assets/styles/CommonSxProps";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import { LstDadosContasCadastradas } from "../../../../interfaces/entities/Reembolso/DadosContaBeneficiario";
import { AccountModalProps } from "../../../../interfaces/props/Financeiro/Reembolso/AccountModalProps";
import { NewBankAccountValidation } from "../../../../interfaces/validation/NewBankAccountValidation";
import { cpfMask } from "../../../../utils/CpfMask";
import {
  accountWithDigitMask,
  onlyNumbers,
} from "../../../../utils/OnlyNumbers";
import AccountFilter from "./AccountsFilters/AccountFilter";
import AccountTypeFilter from "./AccountsFilters/AccountTypeFilter";
import BankFilter from "./AccountsFilters/BankFilter";
import { defaultAccountsOptions } from "./defaultStates";
import "./styles.css";
import {
  sxButton,
  sxInput,
  sxLabel,
  sxModal,
  sxOutlinedButton,
} from "./sxProps";

function AccountModal(props: AccountModalProps) {
  const { account, open, handleClose, dadosContaBeneficiario } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);

  const numContaOnlyNumbers = onlyNumbers(
    (account?.numConta || "") + (account?.digConta || "")
  );

  const formik = useFormik<LstDadosContasCadastradas>({
    initialValues: {
      indClassifConj: account?.indClassifConj || "",
      nomeTitularConta: account?.nomeTitularConta || "",
      cpfTitularConta: account?.cpfTitularConta || "",
      numBanco: account?.numBanco || "",
      agencia: account?.agencia || "",
      numConta: numContaOnlyNumbers || "",
      indTipoConta: account?.indTipoConta || "",
      idPessoaDependente: account?.idPessoaDependente || 0,
      idCadContaReembolso: account?.idCadContaReembolso || "",
      digConta: account?.digConta || "",
      idPessoa: account?.idPessoa || "",
      dtCad: account?.dtCad || "",
      dtInicValidade: account?.dtInicValidade || "",
      dtFimValidade: account?.dtFimValidade || "",
      descrTipoConta: account?.descrTipoConta || "",
      banco: account?.banco || "",
    },
    validationSchema: NewBankAccountValidation,
    onSubmit: (values) => {
      handleConfirm(values);
    },
    validateOnChange: false,
  });

  useEffect(() => {
    if (!!account) {
      formik.setValues(account);
    }
  }, [account]);

  const handleAccount = (value: string) => {
    formik.values.indClassifConj = value;
  };

  const handleAccountType = (value: string) => {
    switch (value) {
      case "C":
        formik.values.descrTipoConta = "Corrente";
        formik.values.indTipoConta = value;
        break;
      case "P":
        formik.values.descrTipoConta = "Poupança";
        formik.values.indTipoConta = value;
        break;
      default:
        break;
    }
  };

  const handleBank = (value: string) => {
    const bank = dadosContaBeneficiario.lstBanco.filter(
      (el) => el.numBanco === value
    );
    formik.values.banco = bank[0].nomeBanco;
    formik.values.numBanco = value;
  };

  const closeModalSuccess = () => {
    setModalFeedback(null);
  };

  const handleConfirm = (conta: LstDadosContasCadastradas) => {
    if (
      conta.indTipoConta === "" ||
      conta.nomeTitularConta === "" ||
      conta.cpfTitularConta === "" ||
      conta.numBanco === "" ||
      conta.indClassifConj === "" ||
      conta.numConta === ""
    ) {
      setModalFeedback({
        icon: "error",
        title: "Erro ao cadastrar conta",
        subTitle: "Preencha todos os campos",
        textButton: "Voltar",
        closeAction: closeModalSuccess,
        buttonAction: closeModalSuccess,
        showButtonModal: true,
      });
    } else {
      props.handleConfirm(conta);
      formik.resetForm();
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    mask: ((inputValue: string) => string) | null,
    formik: FormikProps<any>
  ) => {
    const { value } = event.target as HTMLInputElement;
    formik.setFieldValue(field, mask ? mask(value) : value);
  };

  const handleChangeAccountNumber = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    mask: ((inputValue: string) => string) | null,
    formik: FormikProps<any>
  ) => {
    const { value } = event.target as HTMLInputElement;
    const numbersValue = onlyNumbers(value);
    if (numbersValue.length < 4) {
      formik.setFieldValue("numConta", numbersValue);
      formik.setFieldValue("digConta", "");
    } else {
      const charactersArray = numbersValue.split("");
      const digConta = charactersArray.pop();
      const numConta = charactersArray.join("");
      formik.setFieldValue("numConta", mask ? mask(numConta) : numConta);
      formik.setFieldValue("digConta", mask ? mask(digConta!) : digConta);
    }
  };

  const handleCloseModal = () => {
    handleClose()
    formik.resetForm()
  }

  return (
    <div className="account-modal-holder">
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={sxModal}>
          {loading && (
            <div className="loading-holder">
              <CircularProgress />
            </div>
          )}
          {!loading && (
            <form onSubmit={formik.handleSubmit}>
              <div className="account-modal-header">
                <h1 className="account-modal-header-title">
                  Cadastrar nova conta
                </h1>
                <button
                  className="account-modal-header-close"
                  onClick={handleCloseModal}
                  aria-label="fechar"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="account-modal-info">
                <AccountFilter
                  title="Conta"
                  options={defaultAccountsOptions}
                  value={formik.values.indClassifConj}
                  handleChange={handleAccount}
                  disabled={false}
                />
              </div>
              <div className="account-modal-full-info">
                <Typography sx={sxLabel}>Titular da conta</Typography>
                <label htmlFor="titular-conta-input" aria-label="titular da conta" />
                <OutlinedInput
                  fullWidth
                  placeholder="Exemplo: José Oliveira da Silva"
                  id="titular-conta-input"
                  type={"text"}
                  value={formik.values.nomeTitularConta}
                  onChange={(e) =>
                    handleInputChange(e, "nomeTitularConta", null, formik)
                  }
                  name="nomeTitularConta"
                  error={false}
                  maxRows={1}
                  sx={sxInput}
                  inputProps={{ maxLength: 60 }}
                />
              </div>
              <div className="account-modal-info">
                <Typography sx={sxLabel}>CPF do titular</Typography>
                <label htmlFor="titular-cpf-input" aria-label="cpf do titular" />
                <OutlinedInput
                  fullWidth
                  placeholder="Exemplo: 99999999999"
                  id="titular-cpf-input"
                  type={"text"}
                  value={formik.values.cpfTitularConta}
                  onChange={(e) =>
                    handleInputChange(e, "cpfTitularConta", cpfMask, formik)
                  }
                  name="cpfTitularConta"
                  error={!!formik.errors.cpfTitularConta}
                  maxRows={1}
                  inputProps={{ maxLength: 14 }}
                  sx={sxInput}
                />
                <Typography sx={sxFormError}>
                  {formik.errors.cpfTitularConta}
                </Typography>
              </div>
              <div className="account-modal-full-info">
                <BankFilter
                  title="Banco"
                  options={dadosContaBeneficiario.lstBanco}
                  value={formik.values.numBanco}
                  handleChange={handleBank}
                  disabled={false}
                />
              </div>
              <div className="account-modal-double-info">
                <div className="account-modal-double-info-field">
                  <Typography sx={sxLabel}>Agência</Typography>
                  <label htmlFor="agencia-input" aria-label="agência" />
                  <OutlinedInput
                    id="agencia-input"
                    fullWidth
                    placeholder="Exemplo: 1111"
                    type={"text"}
                    value={formik.values.agencia}
                    onChange={(e) =>
                      handleInputChange(e, "agencia", null, formik)
                    }
                    name="agencia"
                    error={false}
                    maxRows={1}
                    sx={sxInput}
                    inputProps={{ maxLength: 4 }}
                  />
                </div>
                <div className="account-modal-double-info-field">
                  <Typography sx={sxLabel}>Número de conta</Typography>
                  <label htmlFor="numero-conta-input" aria-label="número da conta" />
                  <OutlinedInput
                    id="numero-conta-input"
                    fullWidth
                    placeholder="Exemplo: 1111111"
                    type={"text"}
                    value={
                      formik.values.numConta.length < 3
                        ? formik.values.numConta
                        : accountWithDigitMask(
                            formik.values.numConta + formik.values.digConta
                          )
                    }
                    onChange={(e) => {
                      handleChangeAccountNumber(e, onlyNumbers, formik);
                    }}
                    name="numConta"
                    error={false}
                    maxRows={1}
                    sx={sxInput}
                    inputProps={{ maxLength: 20 }}
                  />
                </div>
              </div>
              <div className="account-modal-full-info">
                <AccountTypeFilter
                  title="Tipo de conta"
                  options={dadosContaBeneficiario.lstTipoConta}
                  value={formik.values.indTipoConta}
                  handleChange={handleAccountType}
                  disabled={false}
                />
              </div>
              <div className="account-modal-buttons">
                <div className="account-modal-button">
                  <Button
                    variant="outlined"
                    onClick={handleCloseModal}
                    sx={sxOutlinedButton}
                    disabled={false}
                    fullWidth
                  >
                    <Typography style={{ color: "#00995D" }}>
                      Cancelar
                    </Typography>
                  </Button>
                </div>
                <div className="account-modal-button">
                  <Button
                    variant="contained"
                    type="submit"
                    sx={sxButton}
                    disabled={false}
                    fullWidth
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            </form>
          )}
        </Box>
      </Modal>
      <ModalFeedBack
        showModal={!!modalFeedback}
        icon={modalFeedback?.icon as "success" | "error"}
        textButton={modalFeedback?.textButton as string}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        showButtonModal={modalFeedback?.showButtonModal}
        handleShowModal={modalFeedback?.closeAction}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </div>
  );
}

export default AccountModal;
